/* eslint-disable react-hooks/exhaustive-deps */
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  cliffStartBlock,
  fancyBearsContract,
  honeyJarsContract,
  honeyVestingContract,
} from 'config';
import useConnectedAccount from 'hooks/useConnectedAccount';
import { useProvider } from 'wagmi';
import EventsContext, { EventsContextValue } from 'contexts/EventsContext';
import AssetsContext from 'contexts/AssetsContext';
import { Contract, Event } from 'ethers';

const EventsProvider: FunctionComponent = ({ children }) => {
  const [address] = useConnectedAccount();
  const provider = useProvider();
  const { fancyBearsInWallet, honeyJarsInWallet } = useContext(AssetsContext);
  const [transferEvents, setTransferEvents] =
    useState<EventsContextValue['events']>();
  const [vestingEvents, setVestingEvents] =
    useState<EventsContextValue['events']>();
  const events = useMemo(
    () =>
      transferEvents && vestingEvents
        ? [...transferEvents, ...vestingEvents].sort(
            (a, b) => b.block.timestamp - a.block.timestamp
          )
        : undefined,
    [transferEvents, vestingEvents]
  );

  const mapEvents = useCallback(async (contract: Contract, events: Event[]) => {
    const blocks = await Promise.all(events.map(event => event.getBlock()));

    return events.map((log, i) => ({
      hash: log.transactionHash,
      contractAddress: contract.address,
      log: contract.interface.parseLog({
        topics: log.topics,
        data: log.data,
      }),
      block: blocks[i],
    }));
  }, []);

  const fetchTokenEvents = useCallback(async () => {
    if (!address) {
      return;
    }

    try {
      const tokenContracts = [fancyBearsContract, honeyJarsContract];
      const transferEventsResults = await Promise.all(
        tokenContracts
          .map(contract => {
            const connectedContract = contract.connect(provider);

            return [
              connectedContract.queryFilter(
                connectedContract.filters.Transfer(address),
                cliffStartBlock
              ),
              connectedContract.queryFilter(
                connectedContract.filters.Transfer(null, address),
                cliffStartBlock
              ),
            ];
          })
          .flat()
      );

      setTransferEvents(
        (
          await Promise.all(
            tokenContracts.map(
              async (contract, i) =>
                await mapEvents(contract, [
                  ...transferEventsResults[i * 2],
                  ...transferEventsResults[i * 2 + 1],
                ])
            )
          )
        ).flat()
      );
    } catch (err) {
      console.error(err);
    }
  }, [address]);

  useEffect(() => {
    fetchTokenEvents();
  }, [fetchTokenEvents]);

  const fetchVestingEvents = useCallback(async () => {
    if (!address || !fancyBearsInWallet || !honeyJarsInWallet) {
      return;
    }

    if (fancyBearsInWallet.length === 0 && honeyJarsInWallet.length === 0) {
      setVestingEvents([]);
      return;
    }

    try {
      const contract = honeyVestingContract.connect(provider);
      const events = (
        await Promise.all([
          ...(fancyBearsInWallet.length > 0
            ? [
                contract.queryFilter(
                  contract.filters.HoneyReleasedFromBear(
                    fancyBearsInWallet.map(([id]) => id)
                  ),
                  cliffStartBlock
                ),
                contract.queryFilter(
                  contract.filters.HoneySpentFromBear(
                    fancyBearsInWallet.map(([id]) => id)
                  ),
                  cliffStartBlock
                ),
              ]
            : []),
          ...(honeyJarsInWallet.length > 0
            ? [
                contract.queryFilter(
                  contract.filters.HoneyReleasedFromJar(
                    honeyJarsInWallet.map(id => id)
                  ),
                  cliffStartBlock
                ),
                contract.queryFilter(
                  contract.filters.HoneySpentFromJar(
                    honeyJarsInWallet.map(id => id)
                  ),
                  cliffStartBlock
                ),
              ]
            : []),
        ])
      ).flat();

      setVestingEvents(await mapEvents(honeyVestingContract, events));
    } catch (err) {
      console.error(err);
    }
  }, [address, fancyBearsInWallet, honeyJarsInWallet]);

  useEffect(() => {
    fetchVestingEvents();
  }, [fetchVestingEvents]);

  const fetchEvents = useCallback(() => {
    fetchTokenEvents();
    fetchVestingEvents();
  }, [fetchTokenEvents, fetchVestingEvents]);

  const contextValue = useMemo(
    () => ({
      events,
      fetchEvents,
    }),
    [events, fetchEvents]
  );

  return (
    <EventsContext.Provider value={contextValue}>
      {children}
    </EventsContext.Provider>
  );
};

export default EventsProvider;
